import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import styles from './Modal.module.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  /**
   * Default `false` (REGRA DE UI do GAIA): clique no backdrop NÃO fecha o modal,
   * evitando perda acidental de formulários longos. Só ative em modais leves
   * sem state em risco (ex: visualização rápida sem campos editáveis).
   * ESC e botão X / Cancelar continuam fechando normalmente.
   */
  closeOnBackdropClick?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Quando `true`, ESC e clique no backdrop NÃO fecham.
   * Usar pra modais bloqueantes (ex: PasswordExpiredGate na Fase 1C).
   */
  blocking?: boolean;
  /**
   * Quando `true` e viewport `<=768px`, o painel ocupa a tela inteira
   * (sem padding lateral, max-width/height 100%, border-radius 0).
   * Default `false` pra preservar UX de confirmacoes pequenas.
   * Use em modais grandes: drawers, formularios complexos.
   */
  mobileFull?: boolean;
};

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = false,
  blocking = false,
  mobileFull = false,
  children,
  footer,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const dialog = ref.current;
    if (!dialog) return;

    function onCancel(event: Event): void {
      // O evento `cancel` de um <input type=file> filho BORBULHA até o <dialog>
      // (ex.: usuário abre o seletor de arquivo e cancela com Esc). Esse cancel
      // não é um pedido de fechar o modal — só o close-request do próprio dialog
      // (Esc no modal) tem `target === dialog`. Ignorar os que vêm de descendentes.
      if (event.target !== dialog) return;
      event.preventDefault();
      if (!blocking) {
        onClose();
      }
    }
    dialog.addEventListener('cancel', onCancel);
    return () => dialog.removeEventListener('cancel', onCancel);
  }, [open, onClose, blocking]);

  const pointerDownOnBackdrop = useRef(false);

  function handlePointerDown(event: React.PointerEvent<HTMLDialogElement>): void {
    pointerDownOnBackdrop.current = event.target === ref.current;
  }

  function handleBackdropClick(event: React.MouseEvent<HTMLDialogElement>): void {
    if (!closeOnBackdropClick || blocking) {
      pointerDownOnBackdrop.current = false;
      return;
    }
    // Só fecha se o clique COMEÇOU e TERMINOU no backdrop.
    // Evita fechar quando o user seleciona texto dentro do modal e
    // solta o mouse fora do panel (drag-to-select).
    const startedOnBackdrop = pointerDownOnBackdrop.current;
    pointerDownOnBackdrop.current = false;
    if (startedOnBackdrop && event.target === ref.current) {
      onClose();
    }
  }

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onPointerDown={handlePointerDown}
      onClick={handleBackdropClick}
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div className={`${styles.panel} ${styles[`size-${size}`]} ${mobileFull ? styles['mobile-full'] : ''}`}>
        {(title || (showCloseButton && !blocking)) && (
          <div className={styles.header}>
            {title ? (
              <h2 id="modal-title" className={styles.title}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showCloseButton && !blocking ? (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            ) : null}
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </dialog>
  );
}
